import {
    Box, Grid, Button, Typography, IconButton, Menu, MenuItem, Chip, Skeleton,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import React from 'react';
import './scss/print-group.styles.scss';
import { apiManage_rbp as apiManage } from '../../request';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import DialogComponent from '../../component/dialog/dialog.component';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import GlobalNotification from '../../component/common/snackbar';
import _ from 'lodash';
import i18n from '../../i18n';
import { PrintModuleAuxiliaryModeContext } from './print-module-auxiliary-mode-context';
import {
    PrintDetailDeletePrinterButton,
    PrintDetailHeadActions,
    PrintDetailSaveButton,
} from './components/print-detail-head-buttons';

enum PrintGroupDialogType {
    ADD_PRINT_DEVICE = 'ADD_PRINT_DEVICE',
}

class PrintGroupPage extends React.Component<Props, State> {
    static contextType = PrintModuleAuxiliaryModeContext;
    context!: React.ContextType<typeof PrintModuleAuxiliaryModeContext>;

    constructor(props: Props) {
        super(props);

        this.onSave = _.debounce(this.onSave, 500, {
            leading: true,
            trailing: false,
        }).bind(this) as any;
    }

    public state: State = {
        printGroups: [],
        activeMenu: '',
        printGroupName: '',
        printGroupId: '',
        printGroupDialog: false,
        printGroupDialogType: undefined,
        selectedPrintDevice: [],
        allPrintDevice: [],
        printerRecords: [],
        addPrintGroupName: '',
        listItemMenuAnchor: null,
        listItemMenuDevice: null as any,
        addPrinterMenuAnchor: null,
        pageLoading: true,
        saveSubmitting: false,
        /** 删除打印组：与打印头页一致使用 AlertDialog */
        deleteAlertOpen: false,
        deleteTarget: null as any,
        deleteSubmitting: false,
    };

    componentDidMount() {
        this.getPrinterClass();
    }

    /**
     * 与打印机设置页一致：由连接方式 + 地址生成列表副标题
     */
    private getDeviceSubtitle(ds: any): string {
        const mode = `${ds?.check_info_print ?? 0}`;
        const modeLabelMap: Record<string, string> = {
            '0': i18n.t('print_setting_page_serial_port'),
            '1': i18n.t('print_setting_page_parallel_port'),
            '3': i18n.t('print_setting_page_net_connection'),
            '4': i18n.t('print_setting_page_device_drive_program'),
            '5': i18n.t('print_setting_page_compatibility_mode'),
            '6': i18n.t('print_setting_page_tcp_passthrough'),
        };
        const label = modeLabelMap[mode] || '';
        const name = (ds?.printer_name || '').trim();
        if (['0', '1'].includes(mode)) {
            const port = ds?.com_port;
            const tail = port != null && port !== '' ? `COM ${port}` : '';
            return tail ? `${label} • ${tail}` : label;
        }
        return name ? `${label} • ${name}` : label;
    }

    private getPrintClassListSubtitle(pc: any): string {
        const arr = pc?.devices_arr || [];
        if (!arr.length) {
            return '';
        }
        const firstId = arr[0]?.print_device_id;
        const dev = this.state.printerRecords.find(
            (p: any) => String(p.print_device_id) === String(firstId),
        );
        return dev ? this.getDeviceSubtitle(dev) : '';
    }

    private closeListMenu = () => {
        this.setState({ listItemMenuAnchor: null, listItemMenuDevice: null });
    };

    private openListMenu = (e: React.MouseEvent<HTMLElement>, row: any) => {
        e.stopPropagation();
        this.setState({ listItemMenuAnchor: e.currentTarget, listItemMenuDevice: row });
    };

    async getPrinterClass() {
        try {
            const [res, deviceRes] = await Promise.all([
                apiManage.getPrinterClass(),
                apiManage.getPrinterDevice({}),
            ]);
            // 设备列表与打印机组无接口依赖；副标题用 printerRecords 在下一轮渲染即可补齐
            if (!deviceRes?.error && Array.isArray(deviceRes)) {
                const allPrintDevice = deviceRes.map((item: any) => ({
                    label: item?.print_device_name || '',
                    value: item?.print_device_id || '',
                }));
                this.setState({
                    allPrintDevice,
                    printerRecords: deviceRes,
                });
            }
            if (!res?.error) {
                const printGroups = res?.map((item: any) => ({
                    title: item?.print_class_name,
                    key: `${item?.print_class_id}`,
                    dataSource: item,
                }));
                const activeDeviceItem = res.find((item: any) => item?.print_class_id === this.state.activeMenu);
                const deviceItem = activeDeviceItem ? activeDeviceItem : res?.[0];
                this.setState({
                    printGroups,
                    activeMenu: deviceItem?.print_class_id || '',
                });
                this.getPrinterClassItem(deviceItem);
            }
        } finally {
            this.setState({ pageLoading: false });
        }
    }

    async getPrinterClassItem(item: any) {
        if (!item) {
            return;
        }
        const selectedPrintDevice = (item?.devices_arr || []).map((iitem: any) => `${iitem?.print_device_id}`);
        this.setState({
            selectedPrintDevice,
            printGroupId: item?.print_class_id,
            printGroupName: item?.print_class_name,
            activeMenu: item?.print_class_id,
        });
    }

    /** 顶部「+」打开新建打印组弹窗 */
    openPrintGroupAddDialog = () => {
        this.setState({
            printGroupDialog: true,
            printGroupDialogType: PrintGroupDialogType.ADD_PRINT_DEVICE,
            addPrintGroupName: '',
            printGroupId: '',
        });
    };

    /** 列表菜单 / 详情「删除打印组」确认后提交 */
    confirmDeletePrintGroup = async () => {
        const id = this.state.deleteTarget?.print_class_id;
        if (id == null) {
            return;
        }
        this.setState({ deleteSubmitting: true });
        try {
            const res = await apiManage.delPrinterClass([Number(id)]);
            if (!_.get(res, 'error')) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                this.setState({
                    deleteAlertOpen: false,
                    deleteTarget: null,
                    printGroupId: '',
                    printGroupName: '',
                });
                await this.getPrinterClass();
            } else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
        } finally {
            this.setState({ deleteSubmitting: false });
        }
    };

    dialogConfig(action: 'CONTENT' | 'TITLE' | 'FUN' = 'FUN') {
        const { printGroupDialogType } = this.state;
        let content = <div />;
        let fun = async () => { };
        let title = '';
        switch (printGroupDialogType) {
            case 'ADD_PRINT_DEVICE':
                if (action === 'CONTENT') {
                    content = (
                        <FormFieldCell labelKey='print_group_and_KWA_job_page_printer_group_name'>
                            <CommonInput
                                variant='outlined'
                                fullWidth
                                hiddenLabel
                                regularType='required'
                                value={this.state.addPrintGroupName}
                                onChange={(e) => {
                                    this.setState({ addPrintGroupName: e.target.value });
                                }}
                            />
                        </FormFieldCell>
                    );
                } else if (action === 'TITLE') {
                    title = i18n.t('print_group_and_KWA_job_page_print_group_info');
                } else {
                    fun = async () => {
                        if (_.trim(this.state.addPrintGroupName) === '') {
                            GlobalNotification.warning(i18n.t('staff_setting_page_enter_type_name'));
                            return;
                        }
                        const res = await apiManage.postPrinterClass({ print_class_name: this.state.addPrintGroupName });
                        if (!_.get(res, 'error')) {
                            GlobalNotification.success(i18n.t('global_text_operate_success'));
                            this.setState({
                                printGroupDialog: false,
                                printGroupDialogType: undefined,
                            });
                            this.getPrinterClass();
                        } else {
                            GlobalNotification.error(i18n.t('global_text_operate_failure'));
                        }
                    };
                }
                break;
            default:
                break;
        }
        if (action === 'CONTENT') {
            return content;
        }
        if (action === 'TITLE') {
            return title;
        }
        if (action === 'FUN') {
            return fun;
        }
    }

    async onSave() {
        const { activeMenu, printGroupName, allPrintDevice, selectedPrintDevice } = this.state;
        if (!_.trim(printGroupName)) {
            GlobalNotification.error(i18n.t('print_group_and_KWA_job_page_type_printer_group_name'));
            return;
        }
        this.setState({ saveSubmitting: true });
        try {
            const devices_arr: any[] = [];
            allPrintDevice.forEach((item) => {
                if (selectedPrintDevice.includes(String(item.value))) {
                    devices_arr.push({
                        print_device_id: item.value,
                        print_class_id: Number(activeMenu),
                    });
                }
            });
            const res = await apiManage.patchPrinterClass(String(activeMenu), {
                class: { print_class_name: printGroupName },
                devices: devices_arr,
            });
            if (!res?.error) {
                this.getPrinterClass();
                GlobalNotification.success(i18n.t('google_text_edit_succeed'));
            }
        } finally {
            this.setState({ saveSubmitting: false });
        }
    }

    private removePrinter = (deviceId: string) => {
        this.setState({
            selectedPrintDevice: this.state.selectedPrintDevice.filter((id) => id !== deviceId),
        });
    };

    private addPrinter = (deviceId: string) => {
        const s = new Set(this.state.selectedPrintDevice.map(String));
        s.add(String(deviceId));
        this.setState({ selectedPrintDevice: Array.from(s), addPrinterMenuAnchor: null });
    };

    /** 详情头删除：AlertDialog 确认（与打印头设置一致） */
    private openDeleteCurrentPrintGroupDialog = () => {
        const row = this.state.printGroups.find((p) => String(p.key) === String(this.state.activeMenu));
        if (!row?.dataSource) {
            return;
        }
        this.setState({ deleteAlertOpen: true, deleteTarget: row.dataSource });
    };

    render() {
        const {
            printGroups,
            activeMenu,
            printGroupDialog,
            printGroupName,
            selectedPrintDevice,
            allPrintDevice,
            listItemMenuAnchor,
            listItemMenuDevice,
            addPrinterMenuAnchor,
            pageLoading,
            saveSubmitting,
            deleteAlertOpen,
            deleteSubmitting,
        } = this.state;

        if (pageLoading) {
            return <PrintGroupPageSkeleton />;
        }

        const fieldHint = i18n.t('print_native_protocol_page_param_hint');
        const availableToAdd = allPrintDevice.filter(
            (d) => !selectedPrintDevice.map(String).includes(String(d.value)),
        );

        return (
            <Box className='print-group-page print-group-page--shell'>
                <Grid className='print-group-page__layout' container spacing={3}>
                    <Grid item className='print-group-page__list-column'>
                        <Box className='print-group-page__list-card'>
                            <Box className='print-group-page__list-head'>
                                <Typography component='div' className='print-group-page__list-title'>
                                    {i18n.t('print_group_page_kwa_list_title')}
                                </Typography>
                                <IconButton
                                    size='small'
                                    className='print-group-page__list-add'
                                    onClick={() => {
                                        this.openPrintGroupAddDialog();
                                    }}
                                    aria-label={i18n.t('google_text_newly_increased')}
                                >
                                    <Add />
                                </IconButton>
                            </Box>
                            <Box className='print-group-page__list-body'>
                                {printGroups.map((row: any) => {
                                    const isActive = String(activeMenu) === String(row.key);
                                    return (
                                        <Box
                                            key={row.key}
                                            className={
                                                isActive
                                                    ? 'print-group-page__list-item print-group-page__list-item--active'
                                                    : 'print-group-page__list-item'
                                            }
                                            onClick={() => {
                                                this.getPrinterClassItem(row.dataSource);
                                            }}
                                        >
                                            <Box className='print-group-page__list-item-text'>
                                                <Typography component='div' className='print-group-page__list-item-name'>
                                                    {row.title}
                                                </Typography>
                                                <Typography component='div' className='print-group-page__list-item-sub'>
                                                    {this.getPrintClassListSubtitle(row.dataSource)}
                                                </Typography>
                                            </Box>
                                            {/* <IconButton
                                                size='small'
                                                className='print-group-page__list-item-more'
                                                onClick={(e) => {
                                                    this.openListMenu(e, row.dataSource);
                                                }}
                                                aria-label={i18n.t('google_text_public_button_delete')}
                                            >
                                                <MoreVert />
                                            </IconButton> */}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Grid>
                    {_.isEmpty(printGroups) ? null : (
                        <Grid item xs className='print-group-page__detail-column' minWidth={0}>
                            <Box className='print-group-page__detail-card'>
                                <Box className='print-group-page__detail-head'>
                                    <Typography component='div' className='print-group-page__detail-title'>
                                        {i18n.t('print_group_page_kwa_detail_title')}
                                    </Typography>
                                    <PrintDetailHeadActions className='print-group-page__detail-actions'>
                                        <PrintDetailDeletePrinterButton
                                            className='print-group-page__btn-delete-printer'
                                            label={i18n.t('print_detail_delete_print_group')}
                                            disabled={!activeMenu}
                                            onClick={() => this.openDeleteCurrentPrintGroupDialog()}
                                        />
                                        <PrintDetailSaveButton
                                            className='print-group-page__btn-save'
                                            loading={saveSubmitting}
                                            onClick={() => void this.onSave()}
                                        >
                                            {i18n.t('job_settings_save')}
                                        </PrintDetailSaveButton>
                                    </PrintDetailHeadActions>
                                </Box>
                                <Box className='print-group-page__detail-body'>
                                    <Box className='print-group-page__field'>
                                        <Typography component='div' className='print-group-page__field-label'>
                                            {i18n.t('global_text_name')}
                                        </Typography>
                                        {this.context?.auxiliaryModeEnabled ? (
                                            <Typography component='div' className='print-group-page__field-hint'>
                                                {fieldHint}
                                            </Typography>
                                        ) : null}
                                        <CommonInput
                                            variant='outlined'
                                            fullWidth
                                            hiddenLabel
                                            required
                                            value={printGroupName}
                                            onChange={(e) => {
                                                this.setState({ printGroupName: e.target.value });
                                            }}
                                        />
                                    </Box>
                                    <Box className='print-group-page__printer-section'>
                                        <Typography component='div' className='print-group-page__field-label'>
                                            {i18n.t('print_group_and_KWA_job_page_select_printer')}
                                        </Typography>
                                        {this.context?.auxiliaryModeEnabled ? (
                                            <Typography component='div' className='print-group-page__field-hint'>
                                                {i18n.t('print_group_page_select_printer_hint')}
                                            </Typography>
                                        ) : null}
                                        <Box className='print-group-page__printer-tags'>
                                            {selectedPrintDevice.map((id) => {
                                                const dev = allPrintDevice.find((d) => String(d.value) === String(id));
                                                return (
                                                    <Chip
                                                        key={id}
                                                        className='print-group-page__printer-chip'
                                                        label={dev?.label || id}
                                                        onDelete={() => {
                                                            this.removePrinter(String(id));
                                                        }}
                                                    />
                                                );
                                            })}
                                            <Button
                                                className='print-group-page__add-printer'
                                                startIcon={<Add />}
                                                onClick={(e) => {
                                                    this.setState({ addPrinterMenuAnchor: e.currentTarget });
                                                }}
                                                disabled={availableToAdd.length === 0}
                                            >
                                                {i18n.t('print_group_page_add_printer_chip')}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
                <Menu
                    anchorEl={listItemMenuAnchor}
                    open={Boolean(listItemMenuAnchor)}
                    onClose={this.closeListMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem
                        onClick={() => {
                            this.setState({ deleteAlertOpen: true, deleteTarget: listItemMenuDevice });
                            this.closeListMenu();
                        }}
                    >
                        {i18n.t('google_text_public_button_delete')}
                    </MenuItem>
                </Menu>
                <Menu
                    anchorEl={addPrinterMenuAnchor}
                    open={Boolean(addPrinterMenuAnchor)}
                    onClose={() => {
                        this.setState({ addPrinterMenuAnchor: null });
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    {availableToAdd.map((d) => (
                        <MenuItem
                            key={String(d.value)}
                            onClick={() => {
                                this.addPrinter(String(d.value));
                            }}
                        >
                            {d.label}
                        </MenuItem>
                    ))}
                </Menu>
                <AlertDialog
                    open={deleteAlertOpen}
                    variant="delete"
                    title={i18n.t('dinner_table_page_confirm_delete')}
                    desc={i18n.t('dinner_table_page_whether_delete')}
                    size='xs'
                    confirmLoading={deleteSubmitting}
                    onCancel={() => {
                        this.setState({ deleteAlertOpen: false, deleteTarget: null });
                    }}
                    onConfirm={() => void this.confirmDeletePrintGroup()}
                />
                <DialogComponent
                    open={printGroupDialog}
                    fullWidth={false}
                    onClose={() => {
                        this.setState({ printGroupDialog: false });
                    }}
                    defaultHeaderProps={{
                        title: this.dialogConfig('TITLE') as string,
                        onClose: () => {
                            this.setState({ printGroupDialog: false });
                        },
                    }}
                    footer={
                        <div>
                            <Button
                                color='primary'
                                variant='outlined'
                                onClick={() => {
                                    this.setState({ printGroupDialog: false });
                                }}
                            >
                                {i18n.t('google_text_public_button_cancel')}
                            </Button>
                            <Box marginLeft={4} display='inline-block'>
                                <Button
                                    color='primary'
                                    variant='contained'
                                    onClick={async () => {
                                        const fun = this.dialogConfig();
                                        if (typeof fun === 'function') {
                                            await fun();
                                        }
                                    }}
                                >
                                    {i18n.t('google_text_confirm')}
                                </Button>
                            </Box>
                        </div>
                    }
                >
                    <Grid style={{ padding: '8px 8px' }}>
                        {this.dialogConfig('CONTENT') as React.ReactNode}
                    </Grid>
                </DialogComponent>
            </Box>
        );
    }
}

/**
 * KWA岗位与打印组 — 首屏骨架（与本页同文件，不单独拆文件）
 */
function PrintGroupPageSkeleton() {
    return (
        <Box className='print-group-page print-group-page--shell print-group-page--skeleton'>
            <Grid className='print-group-page__layout' container spacing={3}>
                <Grid item className='print-group-page__list-column'>
                    <Box className='print-group-page__list-card'>
                        <Box className='print-group-page__list-head'>
                            <Skeleton className='print-group-page__skeleton-title' variant='text' width={160} height={28} />
                            <Skeleton variant='circular' width={32} height={32} />
                        </Box>
                        <Box className='print-group-page__list-body print-group-page__skeleton-list'>
                            {[1, 2, 3, 4, 5, 6, 7].map((k) => (
                                <Skeleton key={k} className='print-group-page__skeleton-list-row' variant='rounded' height={72} />
                            ))}
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs className='print-group-page__detail-column' minWidth={0}>
                    <Box className='print-group-page__detail-card'>
                        <Box className='print-group-page__detail-head'>
                            <Skeleton variant='text' width={180} height={28} />
                            <Box display='flex' sx={{ gap: 3 }}>
                                <Skeleton variant='rounded' width={120} height={44} />
                                <Skeleton variant='rounded' width={140} height={44} />
                            </Box>
                        </Box>
                        <Box className='print-group-page__detail-body'>
                            <Box className='print-group-page__field'>
                                <Skeleton variant='text' width='35%' height={22} />
                                <Skeleton variant='text' width='55%' height={20} sx={{ mt: 0.5 }} />
                                <Skeleton variant='rounded' width='100%' height={56} sx={{ maxWidth: 400, mt: 1 }} />
                            </Box>
                            <Box className='print-group-page__printer-section'>
                                <Skeleton variant='text' width='28%' height={22} />
                                <Skeleton variant='text' width='72%' height={20} sx={{ mt: 0.5 }} />
                                <Skeleton
                                    className='print-group-page__skeleton-tags'
                                    variant='rounded'
                                    height={120}
                                    sx={{ mt: 1, width: '100%' }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default PrintGroupPage;

type State = {
    printGroups: any[];
    printGroupName: string;
    printGroupId: string;
    printGroupDialog: boolean;
    printGroupDialogType: PrintGroupDialogType | undefined;
    activeMenu: string | number;
    selectedPrintDevice: string[];
    allPrintDevice: any[];
    /** getPrinterDevice 原始列表，用于左侧副标题 */
    printerRecords: any[];
    addPrintGroupName: string;
    listItemMenuAnchor: HTMLElement | null;
    listItemMenuDevice: any;
    addPrinterMenuAnchor: HTMLElement | null;
    pageLoading: boolean;
    saveSubmitting: boolean;
    deleteAlertOpen: boolean;
    deleteTarget: any;
    deleteSubmitting: boolean;
};

type Props = Record<string, never>;
